import React, { Component } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo';
import A11yFontIcon from 'components/A11yFontIcon';
import ProfileIcon from 'components/ProfileIcon';
import TextButton from 'components/TextButton';
import { errorShape, userShape } from 'utils/shapes';
import {
  QUERY_GET_USERS_CURRENTLY_PLAYING_GAME,
  SUBSCRIPTION_ADD_USER_CURRENTLY_PLAYING_GAME,
  SUBSCRIPTION_REMOVE_USER_CURRENTLY_PLAYING_GAME
} from './graphql';
import './GamePlayerList.css';

const PLAYERS_PER_PAGE = 5;
const BUTTON_DELAY_MS = 500;

class GamePlayerList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      playerListOffset: 0
    };

    this.scrollPrev = this.scrollPrev.bind(this);
    this.scrollNext = this.scrollNext.bind(this);
    this.buttonTimer = 0;

    this.unsubscribeFromAddUser = undefined;
    this.unsubscribeFromRemoveUser = undefined;
  }

  componentDidMount() {
    this.unsubscribeFromAddUser = this.props.subscribeToAddUser();
    this.unsubscribeFromRemoveUser = this.props.subscribeToRemoveUser();
  }

  componentWillUnmount() {
    if (this.unsubscribeFromAddUser) {
      this.unsubscribeFromAddUser();
      this.unsubscribeFromAddUser = null;
    }
    if (this.unsubscribeFromRemoveUser) {
      this.unsubscribeFromRemoveUser();
      this.unsubscribeFromRemoveUser = null;
    }
  }

  // TODO: If players on visible pages haven't changed, return false
  //shouldComponentUpdate(nextProps, nextState)

  canScrollPrev() {
    return this.state.playerListOffset > 0;
  }

  canScrollNext() {
    const usersCurrentlyPlayingGame =
      this.props.currentPlayersData.getUsersCurrentlyPlayingGame;
    if (!usersCurrentlyPlayingGame || !usersCurrentlyPlayingGame.users) {
      return false;
    }
    const lastOffset =
      usersCurrentlyPlayingGame.users.length - PLAYERS_PER_PAGE;
    return this.state.playerListOffset < lastOffset;
  }

  scrollPrev() {
    if (Date.now() > this.buttonTimer && this.canScrollPrev()) {
      let offset = Math.max(0, this.state.playerListOffset - PLAYERS_PER_PAGE);
      this.setState({ playerListOffset: offset });
      this.buttonTimer = Date.now() + BUTTON_DELAY_MS;
    }
  }

  scrollNext() {
    if (Date.now() > this.buttonTimer && this.canScrollNext()) {
      let offset = this.state.playerListOffset;
      const players =
        this.props.currentPlayersData.getUsersCurrentlyPlayingGame.users;
      const lastOffset = players.length - PLAYERS_PER_PAGE;
      offset = Math.min(lastOffset, offset + PLAYERS_PER_PAGE);
      this.setState({ playerListOffset: offset });
      this.buttonTimer = Date.now() + BUTTON_DELAY_MS;
    }
  }

  appendListPage(result, page, dir) {
    const players =
      this.props.currentPlayersData.getUsersCurrentlyPlayingGame.users;
    if (
      (dir > 0 && !this.canScrollNext()) ||
      (dir < 0 && !this.canScrollPrev()) ||
      !players
    ) {
      return;
    }
    let start = this.state.playerListOffset + dir * PLAYERS_PER_PAGE;
    let end = Math.min(start + PLAYERS_PER_PAGE, players.length);
    start = Math.max(start, 0);
    const offset = dir < 0 ? PLAYERS_PER_PAGE - (end - start) : 0;

    for (let index = start; index < end; ++index) {
      const user = players[index];
      const idx = user.userId;
      const posClass = 'player ' + page + (index - start + offset);
      result.push(
        <div key={idx} className={posClass} title={user.displayName}>
          <ProfileIcon withBorder avatarData={user.avatarData} size={30} />
        </div>
      );
    }
  }

  render() {
    if (!this.props.currentPlayersData) {
      return null;
    }

    const { loading, error, getUsersCurrentlyPlayingGame } =
      this.props.currentPlayersData;

    if (loading || error || !getUsersCurrentlyPlayingGame) {
      return <div className="game-player-list" />;
    }

    const prevDisabled = !this.canScrollPrev();
    const nextDisabled = !this.canScrollNext();

    let players = [];
    this.appendListPage(players, 'prev', -1);
    this.appendListPage(players, 'curr', 0);
    this.appendListPage(players, 'next', +1);

    return (
      <div className="game-player-list">
        <div className="player-count">
          <div className="player-count-value">
            {getUsersCurrentlyPlayingGame.count}
          </div>
          <div className="player-count-label">Playing</div>
        </div>
        <div className="player-list notranslate">
          <TextButton
            onClick={this.scrollPrev}
            disabled={prevDisabled}
            className="scroll-button"
          >
            <A11yFontIcon
              icon="chevron-left"
              alt="Previous"
              className={prevDisabled ? 'fa-disabled' : ''}
            />
          </TextButton>

          <div className="pages">{players}</div>

          <TextButton
            onClick={this.scrollNext}
            disabled={nextDisabled}
            className="scroll-button"
          >
            <A11yFontIcon
              icon="chevron-right"
              alt="Next"
              className={nextDisabled ? 'fa-disabled' : ''}
            />
          </TextButton>
        </div>
      </div>
    );
  }
}

GamePlayerList.propTypes = {
  gameData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  }).isRequired,
  currentPlayersData: PropTypes.shape({
    getUsersCurrentlyPlayingGame: PropTypes.shape({
      users: PropTypes.arrayOf(userShape),
      count: PropTypes.number
    }),
    loading: PropTypes.bool,
    error: errorShape
  })
};

const queryGetUsersCurrentlyPlayingOptions = props => {
  const input = {
    gameId: props.gameData.id
  };

  if (props.tournament && props.tournament.id) {
    input.tournamentId = props.tournament.id;
  }

  return {
    variables: { input: input },
    fetchPolicy: 'network-only'
  };
};

const handleAddUserSubscription = (ownProps, subscribeToMore) => {
  return subscribeToMore({
    document: SUBSCRIPTION_ADD_USER_CURRENTLY_PLAYING_GAME,
    variables: {
      input: {
        gameId: parseInt(ownProps.gameData.id, 10)
      }
    },
    updateQuery: (prev, { subscriptionData: { data } }) => {
      if (
        !data ||
        !data.user ||
        !prev ||
        !prev.getUsersCurrentlyPlayingGame.users
      ) {
        return null;
      }

      let result = cloneDeep(prev);
      const usersPlaying = result.getUsersCurrentlyPlayingGame.users;
      const newlyAddedUser = data.user;
      const userExists = usersPlaying.some(
        user => user.userId === newlyAddedUser.userId
      );

      if (!userExists) {
        usersPlaying.unshift(newlyAddedUser);
        result.getUsersCurrentlyPlayingGame.count += 1;
      }

      return result;
    }
  });
};

const handleRemoveUserSubscription = (ownProps, subscribeToMore) => {
  return subscribeToMore({
    document: SUBSCRIPTION_REMOVE_USER_CURRENTLY_PLAYING_GAME,
    variables: {
      input: {
        gameId: parseInt(ownProps.gameData.id, 10)
      }
    },
    updateQuery: (prev, { subscriptionData: { data } }) => {
      if (
        !data ||
        !data.user ||
        !prev ||
        !prev.getUsersCurrentlyPlayingGame.users
      ) {
        return null;
      }

      let result = cloneDeep(prev);
      const usersPlaying = result.getUsersCurrentlyPlayingGame.users;

      const removeUser = data.user;
      const matchOnUserId = usersPlaying.findIndex(
        p => p.userId === removeUser.userId
      );

      if (matchOnUserId >= 0) {
        usersPlaying.splice(matchOnUserId, 1);
        result.getUsersCurrentlyPlayingGame.count = Math.max(
          result.getUsersCurrentlyPlayingGame.count - 1,
          usersPlaying.length
        );
      }

      return result;
    }
  });
};

export default compose(
  graphql(QUERY_GET_USERS_CURRENTLY_PLAYING_GAME, {
    name: 'currentPlayersData',
    options: queryGetUsersCurrentlyPlayingOptions,
    props: ({ ownProps, currentPlayersData }) => {
      return {
        currentPlayersData,
        subscribeToAddUser: () =>
          handleAddUserSubscription(
            ownProps,
            currentPlayersData.subscribeToMore
          ),
        subscribeToRemoveUser: () =>
          handleRemoveUserSubscription(
            ownProps,
            currentPlayersData.subscribeToMore
          )
      };
    }
  })
)(GamePlayerList);
